import { supabase } from '../utils/supabase';
import { ELO_CONSTANTS, getDefaultStartingElo } from '../utils/constants';
import { initialSignupPlacement, isHostSelectSignup } from '../utils/signupSelection';

/**
 * Fetch the club's default starting ELO (strength_rating), or site default if unset.
 * @param {string} clubId
 * @returns {Promise<number>}
 */
export const fetchClubDefaultStartingElo = async (clubId) => {
  try {
    const { data: club, error } = await supabase
      .from('clubs')
      .select('strength_rating')
      .eq('id', clubId)
      .single();

    if (error) {
      console.warn('[ELO] Could not fetch club strength_rating, using default:', error);
      return ELO_CONSTANTS.DEFAULT_STARTING_ELO;
    }

    return getDefaultStartingElo(club?.strength_rating);
  } catch (error) {
    console.warn('[ELO] Could not fetch club strength_rating, using default:', error);
    return ELO_CONSTANTS.DEFAULT_STARTING_ELO;
  }
};

/**
 * Initialize ELO ratings for a player in a club/season
 * @param {string} playerId - Player ID
 * @param {string} clubId - Club ID  
 * @param {string} seasonId - Season ID
 * @param {Object} initialRatings - Initial ELO ratings (optional)
 * @param {number} initialRatings.mixedElo - Initial mixed doubles ELO (defaults to club setting or site default)
 * @param {number} initialRatings.levelsElo - Initial levels doubles ELO (defaults to club setting or site default)
 * @returns {Promise<void>}
 */
export const initializePlayerEloRatings = async (playerId, clubId, seasonId, initialRatings = {}) => {
  try {
    console.log('[ELO] Initializing ELO ratings for player:', { playerId, clubId, seasonId, initialRatings });

    const clubDefaultElo = await fetchClubDefaultStartingElo(clubId);
    const mixedElo = initialRatings.mixedElo ?? clubDefaultElo;
    const levelsElo = initialRatings.levelsElo ?? clubDefaultElo;
    
    const eloRecord = {
      player_id: playerId,
      club_id: clubId,
      season_id: seasonId,
      mixed_elo_rating: mixedElo,
      levels_elo_rating: levelsElo,
      singles_elo_rating: 1000, // Default singles rating
      mixed_games_played: 0,
      levels_games_played: 0,
      singles_games_played: 0,
      unbalanced_games_played: 0
    };
    
    console.log('[ELO] About to insert ELO record:', eloRecord);
    
    const { data, error } = await supabase
      .from('player_club_elo_ratings')
      .insert([eloRecord])
      .select();

    if (error) {
      console.error('[ELO] Supabase error initializing ELO ratings:', error);
      console.error('[ELO] Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }
    
    console.log('[ELO] ELO ratings initialized successfully, inserted data:', data);
  } catch (error) {
    console.error('[ELO] Error in initializePlayerEloRatings:', error);
    throw error;
  }
};

/**
 * Create a new guest player
 * @param {Object} guestData - Guest information
 * @param {string} guestData.firstName - Guest's first name
 * @param {string} guestData.lastName - Guest's last name  
 * @param {string} guestData.gender - Guest's gender
 * @param {string} guestData.notes - Optional notes about the guest
 * @param {number} guestData.mixedElo - Initial mixed doubles ELO (optional, default: 1200)
 * @param {number} guestData.levelsElo - Initial levels doubles ELO (optional, default: 1200)
 * @param {string} clubId - Club ID where guest is being added
 * @param {string} createdBy - ID of the user creating the guest
 * @param {string} seasonId - Season ID for ELO initialization
 * @returns {Promise<Object>} - The created guest player
 */
export const createGuest = async (guestData, clubId, createdBy, seasonId) => {
  try {
    console.log('[GUEST] Creating guest:', guestData);
    
    // Create guest player record
    const guestRow = {
        first_name: guestData.firstName.trim(),
        last_name: guestData.lastName?.trim() || null,
        gender: guestData.gender,
        is_guest: true,
        guest_created_by: createdBy,
        email: null, // Guests don't have email initially
        auth_id: null // Guests don't have auth accounts
    };
    if (guestData.id) guestRow.id = guestData.id;

    const { data: guest, error: guestError } = await supabase
      .from('players')
      .insert([guestRow])
      .select()
      .single();

    if (guestError) {
      console.error('[GUEST] Error creating guest:', guestError);
      throw guestError;
    }

    // Add guest to club with guest role
    const { error: clubError } = await supabase
      .from('players_club')
      .insert([{
        player_id: guest.id,
        club_id: clubId,
        club_role: 'guest',
        is_active: true
      }]);

    if (clubError) {
      console.error('[GUEST] Error adding guest to club:', clubError);
      throw clubError;
    }

    // Initialize ELO ratings for the guest
    console.log('[GUEST] About to initialize ELO ratings:', { seasonId, guestId: guest.id, clubId, mixedElo: guestData.mixedElo, levelsElo: guestData.levelsElo });
    if (seasonId) {
      try {
        await initializePlayerEloRatings(guest.id, clubId, seasonId, {
          mixedElo: guestData.mixedElo,
          levelsElo: guestData.levelsElo
        });
        console.log('[GUEST] ELO ratings initialized successfully for guest');
      } catch (eloError) {
        console.error('[GUEST] Failed to initialize ELO ratings:', eloError);
        // Don't throw the error - guest creation should still succeed
      }
    } else {
      console.warn('[GUEST] No seasonId provided, skipping ELO initialization');
    }

    console.log('[GUEST] Guest created successfully:', guest);
    return guest;
  } catch (error) {
    console.error('[GUEST] Error in createGuest:', error);
    throw error;
  }
};

/**
 * Add guest directly to a session
 * @param {Object} guestData - Guest information
 * @param {string} sessionId - Session ID to add guest to
 * @param {string} clubId - Club ID where guest is being added
 * @param {string} addedBy - ID of the user adding the guest
 * @param {string} seasonId - Season ID for ELO initialization
 * @param {'signed_up'|'active'} [sessionEntryStatus='signed_up'] - Initial session_players.status
 * @returns {Promise<Object>} - The created guest and signup
 */
export const createGuestAndAddToSession = async (
  guestData,
  sessionId,
  clubId,
  addedBy,
  seasonId,
  sessionEntryStatus = 'signed_up'
) => {
  try {
    console.log('[GUEST] Creating guest and adding to session:', { guestData, sessionId });
    
    // Create the guest
    const guest = await createGuest(guestData, clubId, addedBy, seasonId);

    const { data: sessionRow, error: sessionLookupError } = await supabase
      .from('sessions')
      .select('session_status, signup_selection, max_players, waitlist_enabled')
      .eq('id', sessionId)
      .maybeSingle();
    if (sessionLookupError) {
      console.warn('[GUEST] Could not load session for signup placement:', sessionLookupError);
    }

    const isLive = sessionRow?.session_status === 'ongoing';
    let waitlistCount = 0;
    if (!isLive && isHostSelectSignup(sessionRow)) {
      const { count, error: waitlistCountError } = await supabase
        .from('session_signups')
        .select('id', { count: 'exact', head: true })
        .eq('session_id', sessionId)
        .eq('signup_status', 'waitlist');
      if (waitlistCountError) {
        console.warn('[GUEST] Could not count waitlist for placement:', waitlistCountError);
      } else {
        waitlistCount = count || 0;
      }
    }

    const placement = !isLive
      ? initialSignupPlacement(sessionRow, { waitlistCount, maxPlayers: sessionRow?.max_players })
      : { signupStatus: 'confirmed', waitlistPosition: null };

    // Add guest to session signups and wait for database confirmation
    const { data: signupData, error: signupError } = await supabase
      .from('session_signups')
      .insert([{
        session_id: sessionId,
        player_id: guest.id,
        signup_status: placement.signupStatus || 'confirmed',
        waitlist_position: placement.waitlistPosition,
        signup_date: new Date().toISOString(),
        added_by: addedBy
      }])
      .select()
      .single();

    if (signupError) {
      console.error('[GUEST] Error adding guest to session signups:', signupError);
      throw signupError;
    }

    if (!isLive && isHostSelectSignup(sessionRow)) {
      return {
        ...guest,
        sessionPlayerData: null,
        signupData
      };
    }

    const status =
      sessionEntryStatus === 'active' ? 'active' : 'signed_up';
    const joinedAt = new Date().toISOString();
    const sessionPlayerRow = {
      session_id: sessionId,
      player_id: guest.id,
      club_id: clubId,
      status,
      joined_at: joinedAt
    };
    if (status === 'active') {
      sessionPlayerRow.activated_at = joinedAt;
    }

    // Also add guest to session_players so they appear in Player Management
    const { data: sessionPlayerData, error: sessionPlayerError } = await supabase
      .from('session_players')
      .insert([sessionPlayerRow])
      .select()
      .single();

    if (sessionPlayerError) {
      console.error('[GUEST] Error adding guest to session_players:', sessionPlayerError);
      throw sessionPlayerError;
    }

    console.log('[GUEST] Guest added to session successfully (both signups and session_players confirmed)');
    console.log('[GUEST] Guest status in session_players:', sessionPlayerData?.status, '- Guest is automatically signed up');
    
    // Return guest with session data to confirm both inserts completed
    return {
      ...guest,
      sessionPlayerData,
      signupData
    };
  } catch (error) {
    console.error('[GUEST] Error in createGuestAndAddToSession:', error);
    throw error;
  }
};

function isUniqueViolation(error) {
  return error?.code === '23505' || /duplicate key/i.test(String(error?.message || ''));
}

/**
 * Replay an Offline-mode CREATE_GUEST command using the client UUID so later
 * seat / score / waitlist rows keep the same player id.
 */
export const flushCreateGuestFromOfflineCommand = async (command, { sessionId, clubId }) => {
  if (!command?.playerId) {
    throw new Error('Missing guest player id');
  }
  if (!sessionId || !clubId) {
    throw new Error('Missing session or club');
  }

  const { data: existing, error: lookupError } = await supabase
    .from('players')
    .select('id')
    .eq('id', command.playerId)
    .maybeSingle();
  if (lookupError) throw lookupError;

  if (!existing) {
    try {
      await createGuest({
        id: command.playerId,
        firstName: command.firstName,
        lastName: command.lastName || '',
        gender: command.gender,
        mixedElo: command.mixedElo,
        levelsElo: command.levelsElo,
      }, clubId, command.createdBy, command.seasonId);
    } catch (error) {
      if (!isUniqueViolation(error)) throw error;
    }
  } else {
    const { error: clubError } = await supabase
      .from('players_club')
      .insert([{
        player_id: command.playerId,
        club_id: clubId,
        club_role: 'guest',
        is_active: true,
      }]);
    if (clubError && !isUniqueViolation(clubError)) throw clubError;
    if (command.seasonId) {
      try {
        await initializePlayerEloRatings(command.playerId, clubId, command.seasonId, {
          mixedElo: command.mixedElo,
          levelsElo: command.levelsElo,
        });
      } catch (eloError) {
        if (!isUniqueViolation(eloError)) {
          console.warn('[GUEST] Offline flush ELO skip:', eloError);
        }
      }
    }
  }

  const { error: signupError } = await supabase
    .from('session_signups')
    .insert([{
      session_id: sessionId,
      player_id: command.playerId,
      signup_status: 'confirmed',
      waitlist_position: null,
      signup_date: new Date().toISOString(),
      added_by: command.createdBy || null,
    }]);
  if (signupError && !isUniqueViolation(signupError)) throw signupError;

  const status = command.status === 'active' ? 'active' : 'signed_up';
  const joinedAt = new Date().toISOString();
  const sessionPlayerRow = {
    session_id: sessionId,
    player_id: command.playerId,
    club_id: clubId,
    status,
    exit_reason: null,
    joined_at: joinedAt,
  };
  if (status === 'active') {
    sessionPlayerRow.activated_at = joinedAt;
  }

  const { error: sessionPlayerError } = await supabase
    .from('session_players')
    .upsert([sessionPlayerRow], { onConflict: 'session_id,player_id', ignoreDuplicates: true });
  if (sessionPlayerError && !isUniqueViolation(sessionPlayerError)) throw sessionPlayerError;

  return { id: command.playerId };
};

/**
 * Get soft-deleted guests for a club
 * @param {string} clubId - Club ID
 * @returns {Promise<Array>} - Array of soft-deleted guest players
 */
export const getDeletedClubGuests = async (clubId) => {
  try {
    // Fetch soft-deleted guests data
    const { data: guestsData, error: guestsError } = await supabase
      .from('players')
      .select(`
        *,
        players_club!inner(
          club_id,
          club_role,
          is_active
        ),
        player_club_elo_ratings!left(
          id,
          club_id,
          season_id,
          mixed_elo_rating,
          levels_elo_rating,
          singles_elo_rating,
          mixed_games_played,
          levels_games_played,
          singles_games_played,
          unbalanced_games_played
        ),
        players_session_games!left(
          session_game_id,
          session_games(
            session_id
          )
        )
      `)
      .eq('is_guest', true)
      .eq('players_club.club_id', clubId)
      .eq('players_club.is_active', false)
      .not('deletion_requested_at', 'is', null)
      .is('deleted_at', null);

    if (guestsError) throw guestsError;

    // Process the guests data to aggregate game counts and session counts
    const processedData = (guestsData || []).map(player => {
      const allElo = player.player_club_elo_ratings || [];
      const eloRatings = allElo.filter((r) => r.club_id === clubId);
      const sessionGames = player.players_session_games || [];

      const totalMixedGames = eloRatings.reduce((sum, rating) => sum + (rating.mixed_games_played || 0), 0);
      const totalLevelsGames = eloRatings.reduce((sum, rating) => sum + (rating.levels_games_played || 0), 0);
      const totalSinglesGames = eloRatings.reduce((sum, rating) => sum + (rating.singles_games_played || 0), 0);
      const totalUnbalancedGames = eloRatings.reduce((sum, rating) => sum + (rating.unbalanced_games_played || 0), 0);

      const uniqueSessionIds = new Set(
        sessionGames
          .filter(game => game.session_games?.session_id)
          .map(game => game.session_games.session_id)
      );
      const totalSessions = uniqueSessionIds.size;

      return {
        ...player,
        totalMixedGames,
        totalLevelsGames,
        totalSinglesGames,
        totalUnbalancedGames,
        totalGamesPlayed:
          totalMixedGames + totalLevelsGames + totalSinglesGames + totalUnbalancedGames,
        totalSessions
      };
    });

    return processedData;
  } catch (error) {
    console.error('[GUEST] Error fetching deleted club guests:', error);
    throw error;
  }
};

/**
 * Get declined guest accounts for a club (guests that were reported as incorrect)
 * @param {string} clubId - Club ID
 * @returns {Promise<Array>} - Array of declined guests with merge history details
 */
export const getDeclinedClubGuests = async (clubId) => {
  try {
    // Use database function for scalable, reliable filtering
    // This function filters at the database level, comparing the declined email
    // with the current guest_invited_email to ensure we only show the most recent decline
    const { data: declinedGuests, error: guestsError } = await supabase
      .rpc('get_declined_club_guests', { p_club_id: clubId });

    if (guestsError) {
      console.error('[GUEST] RPC error fetching declined guests:', guestsError);
      throw guestsError;
    }

    console.log('[GUEST] Declined guests from RPC:', declinedGuests?.length || 0, 'guests');

    if (!declinedGuests || declinedGuests.length === 0) {
      return [];
    }

    // Transform the database result to match the expected format
    return declinedGuests.map(guest => ({
      id: guest.id,
      first_name: guest.first_name,
      last_name: guest.last_name,
      guest_invited_email: guest.guest_invited_email,
      guest_merge_declined_at: guest.guest_merge_declined_at,
      guest_merge_declined_by: guest.guest_merge_declined_by,
      declinedByUser: guest.declined_by_user_id ? {
        id: guest.declined_by_user_id,
        first_name: guest.declined_by_user_name?.split(' ')[0] || null,
        last_name: guest.declined_by_user_name?.split(' ').slice(1).join(' ') || null,
        email: guest.declined_by_user_email
      } : null,
      userMessage: guest.user_message,
      declinedAt: guest.declined_at
    }));
  } catch (error) {
    console.error('[GUEST] Error fetching declined guests:', error);
    throw error;
  }
};

export const getClubGuests = async (
  clubId,
  page = 1,
  limit = 10,
  sortField = 'created_at',
  sortDirection = 'desc',
  searchTerm = '',
  seasonId = null
) => {
  try {
    // Calculate offset for pagination
    const offset = (page - 1) * limit;

    // Determine if we need to fetch all data for client-side sorting
    const clientSideSortFields = ['mixedGames', 'levelsGames', 'totalSessions', 'emailStatus'];
    const needsClientSideSort = clientSideSortFields.includes(sortField);
    
    // If searching, we need to fetch all matching records first (no pagination on initial query)
    const isSearching = searchTerm && searchTerm.trim().length > 0;

    let query = supabase
      .from('players')
      .select(`
        *,
        players_club!inner(
          club_id,
          club_role,
          is_active
        ),
        player_club_elo_ratings!left(
          id,
          club_id,
          season_id,
          mixed_elo_rating,
          levels_elo_rating,
          singles_elo_rating,
          mixed_games_played,
          levels_games_played,
          singles_games_played,
          unbalanced_games_played
        ),
        players_session_games!left(
          session_game_id,
          session_games!inner(
            session_id
          )
        )
      `, { count: 'exact' })
      .eq('is_guest', true)
      .eq('players_club.club_id', clubId)
      .eq('players_club.is_active', true)
      .is('deletion_requested_at', null)
      .is('merged_at', null) // Exclude merged guests (kept in DB for audit but not visible)
      .or('guest_merge_declined_at.is.null,and(guest_merge_declined_at.not.is.null,guest_invited_email.is.null)'); 
      // Include:
      // - Guests that are NOT declined (guest_merge_declined_at IS NULL), OR
      // - Guests that ARE declined but don't have an email assigned (declined guests with emails appear in the "Reported Incorrect Guest Accounts" section)
      // This allows declined guests without emails to appear in the main table after email removal

    // Apply search filter if provided
    if (isSearching) {
      const searchLower = searchTerm.trim().toLowerCase();
      query = query.or(`first_name.ilike.%${searchLower}%,last_name.ilike.%${searchLower}%,guest_invited_email.ilike.%${searchLower}%`);
    }

    // Apply server-side sorting for database fields
    if (!needsClientSideSort && sortField) {
      let dbSortField;
      if (sortField === 'name') {
        dbSortField = 'first_name';
      } else if (sortField === 'created') {
        dbSortField = 'created_at';
      } else {
        dbSortField = sortField;
      }
      query = query.order(dbSortField, { ascending: sortDirection === 'asc' });
    } else {
      // For client-side sorting, order by created_at as fallback
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination only if not doing client-side sorting and not searching
    // When searching, we fetch all results first, then paginate after processing
    if (!needsClientSideSort && !isSearching) {
      query = query.range(offset, offset + limit - 1);
    }

    const { data: guestsData, error: guestsError, count } = await query;

    if (guestsError) throw guestsError;

    // Fetch total completed/archived sessions for the club
    const { data: sessionsData, error: sessionsError } = await supabase
      .from('sessions')
      .select('id')
      .eq('club_id', clubId)
      .in('session_status', ['completed', 'archived']);

    if (sessionsError) throw sessionsError;
    
    // Process the guests data to aggregate game counts and session counts
    let processedData = (guestsData || []).map(player => {
      const allEloRows = player.player_club_elo_ratings || [];
      const eloRatings = allEloRows.filter((r) => r.club_id === clubId);
      const sessionGames = player.players_session_games || [];

      // Sum up games for this club only (all seasons)
      const totalMixedGames = eloRatings.reduce((sum, rating) => sum + (rating.mixed_games_played || 0), 0);
      const totalLevelsGames = eloRatings.reduce((sum, rating) => sum + (rating.levels_games_played || 0), 0);
      const totalSinglesGames = eloRatings.reduce((sum, rating) => sum + (rating.singles_games_played || 0), 0);
      const totalUnbalancedGames = eloRatings.reduce((sum, rating) => sum + (rating.unbalanced_games_played || 0), 0);

      const totalGamesPlayed =
        totalMixedGames + totalLevelsGames + totalSinglesGames + totalUnbalancedGames;

      const rawSeasonRow = seasonId
        ? eloRatings.find((r) => r.season_id === seasonId)
        : null;
      const eloForCurrentSeason = rawSeasonRow
        ? {
            id: rawSeasonRow.id,
            mixed_elo_rating: rawSeasonRow.mixed_elo_rating,
            levels_elo_rating: rawSeasonRow.levels_elo_rating,
            singles_elo_rating: rawSeasonRow.singles_elo_rating
          }
        : null;

      const canEditEloRatings = Boolean(seasonId) && totalGamesPlayed === 0;

      // Count unique sessions this player has participated in
      const uniqueSessionIds = new Set(
        sessionGames
          .filter(game => game.session_games?.session_id)
          .map(game => game.session_games.session_id)
      );
      const totalSessions = uniqueSessionIds.size;

      return {
        ...player,
        totalMixedGames,
        totalLevelsGames,
        totalSinglesGames,
        totalUnbalancedGames,
        totalGamesPlayed,
        totalSessions,
        eloForCurrentSeason,
        canEditEloRatings
      };
    });

    // Apply client-side sorting if needed
    if (needsClientSideSort) {
      processedData.sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'mixedGames':
            aValue = a.totalMixedGames || 0;
            bValue = b.totalMixedGames || 0;
            break;
          case 'levelsGames':
            aValue = a.totalLevelsGames || 0;
            bValue = b.totalLevelsGames || 0;
            break;
          case 'totalSessions':
            aValue = a.totalSessions || 0;
            bValue = b.totalSessions || 0;
            break;
          case 'emailStatus':
            aValue = a.guest_invited_email ? 'assigned' : 'no email';
            bValue = b.guest_invited_email ? 'assigned' : 'no email';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      // Apply pagination after sorting
      const startIndex = offset;
      const endIndex = offset + limit;
      processedData = processedData.slice(startIndex, endIndex);
    }
    
    // When searching, we need to paginate after processing all results
    let paginatedData = processedData;
    let totalItems = count || processedData.length;
    
    if (isSearching || needsClientSideSort) {
      // For search or client-side sorting, paginate the processed data
      const startIndex = offset;
      const endIndex = offset + limit;
      paginatedData = processedData.slice(startIndex, endIndex);
      totalItems = processedData.length;
    }
    
    return {
      guests: paginatedData,
      totalClubSessions: sessionsData?.length || 0,
      pagination: {
        currentPage: page,
        itemsPerPage: limit,
        totalItems: totalItems,
        totalPages: Math.ceil(totalItems / limit)
      }
    };
  } catch (error) {
    console.error('[GUEST] Error fetching club guests:', error);
    throw error;
  }
};

/**
 * Get guest activity data for deletion confirmation
 * @param {string} guestId - Guest player ID
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} - Guest activity summary
 */
export const getGuestActivityData = async (guestId, clubId) => {
  try {
    // Fetch guest basic info
    const { data: guestData, error: guestError } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        email,
        gender,
        created_at,
        guest_invited_email,
        player_club_elo_ratings!left(
          mixed_games_played,
          levels_games_played,
          singles_games_played,
          unbalanced_games_played
        ),
        players_session_games!left(
          session_game_id,
          session_games(
            session_id
          )
        )
      `)
      .eq('id', guestId)
      .eq('is_guest', true)
      .single();

    if (guestError) throw guestError;

    // Verify guest belongs to the club
    const { data: clubCheck, error: clubError } = await supabase
      .from('players_club')
      .select('id')
      .eq('player_id', guestId)
      .eq('club_id', clubId)
      .eq('is_active', true)
      .single();

    if (clubError || !clubCheck) {
      throw new Error('Guest does not belong to this club');
    }

    // Process activity data
    const eloRatings = guestData.player_club_elo_ratings || [];
    const sessionGames = guestData.players_session_games || [];
    
    const totalMixedGames = eloRatings.reduce((sum, rating) => sum + (rating.mixed_games_played || 0), 0);
    const totalLevelsGames = eloRatings.reduce((sum, rating) => sum + (rating.levels_games_played || 0), 0);
    const totalSinglesGames = eloRatings.reduce((sum, rating) => sum + (rating.singles_games_played || 0), 0);
    const totalUnbalancedGames = eloRatings.reduce((sum, rating) => sum + (rating.unbalanced_games_played || 0), 0);
    
    const uniqueSessionIds = new Set(
      sessionGames
        .filter(game => game.session_games?.session_id)
        .map(game => game.session_games.session_id)
    );
    
    return {
      ...guestData,
      totalMixedGames,
      totalLevelsGames,
      totalSinglesGames,
      totalUnbalancedGames,
      totalGamesPlayed: totalMixedGames + totalLevelsGames + totalSinglesGames + totalUnbalancedGames,
      totalSessions: uniqueSessionIds.size
    };
  } catch (error) {
    console.error('[GUEST] Error fetching guest activity data:', error);
    throw error;
  }
};

/**
 * Activity at a club for guest identification (last session, recent games).
 * Used by club admin/host when a guest only has one name.
 *
 * @param {string} guestId
 * @param {string} clubId
 * @returns {Promise<{
 *   guest: object,
 *   totals: object,
 *   lastSession: object | null,
 *   recentGames: object[]
 * }>}
 */
export const getGuestIdentificationSummary = async (guestId, clubId) => {
  try {
    const { data: clubCheck, error: clubError } = await supabase
      .from('players_club')
      .select('id')
      .eq('player_id', guestId)
      .eq('club_id', clubId)
      .eq('is_active', true)
      .single();

    if (clubError || !clubCheck) {
      throw new Error('Guest does not belong to this club');
    }

    const { data: guestData, error: guestError } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        gender,
        created_at,
        guest_invited_email,
        player_club_elo_ratings!left(
          club_id,
          mixed_games_played,
          levels_games_played,
          singles_games_played,
          unbalanced_games_played
        )
      `)
      .eq('id', guestId)
      .eq('is_guest', true)
      .single();

    if (guestError) throw guestError;

    const eloRatings = (guestData.player_club_elo_ratings || []).filter(
      (r) => r.club_id === clubId
    );

    const { data: rawRows, error: gamesError } = await supabase
      .from('players_session_games')
      .select(`
        id,
        created_at,
        team_number,
        result,
        pair_type,
        session_games (
          id,
          created_at,
          team_1_score,
          team_2_score,
          winning_team,
          game_type_id,
          session_id,
          club_id,
          game_types:game_type_id ( name ),
          sessions (
            id,
            name,
            session_date,
            start_time,
            session_status,
            club_id
          )
        )
      `)
      .eq('player_id', guestId)
      .order('created_at', { ascending: false })
      .limit(600);

    if (gamesError) throw gamesError;

    const isClubRow = (row) => {
      const sg = row.session_games;
      if (!sg) return false;
      if (sg.session_id && sg.sessions) {
        return sg.sessions.club_id === clubId;
      }
      return sg.club_id === clubId;
    };

    const clubRows = (rawRows || []).filter(isClubRow);

    const totalMixedGames = eloRatings.reduce((sum, r) => sum + (r.mixed_games_played || 0), 0);
    const totalLevelsGames = eloRatings.reduce((sum, r) => sum + (r.levels_games_played || 0), 0);
    const totalSinglesGames = eloRatings.reduce((sum, r) => sum + (r.singles_games_played || 0), 0);
    const totalUnbalancedGames = eloRatings.reduce((sum, r) => sum + (r.unbalanced_games_played || 0), 0);

    const sessionIds = new Set();
    clubRows.forEach((row) => {
      const sid = row.session_games?.session_id;
      if (sid) sessionIds.add(sid);
    });

    const outcomeLabel = (row, sg) => {
      if (row.result === 'win') return 'Win';
      if (row.result === 'loss') return 'Loss';
      if (row.result === 'draw') return 'Draw';
      if (sg.winning_team == null) return '—';
      if (row.team_number === sg.winning_team) return 'Win';
      return 'Loss';
    };

    /** @type {Map<string, { session: object, lastPlayed: string }>} */
    const sessionActivity = new Map();
    clubRows.forEach((row) => {
      const sg = row.session_games;
      if (!sg?.session_id || !sg.sessions) return;
      const sid = sg.session_id;
      const prev = sessionActivity.get(sid);
      const played = row.created_at;
      if (!prev || new Date(played) > new Date(prev.lastPlayed)) {
        sessionActivity.set(sid, { session: sg.sessions, lastPlayed: played });
      }
    });

    let lastSession = null;
    const sessionList = Array.from(sessionActivity.values()).map((v) => v.session);
    sessionList.sort((a, b) => {
      const da = a.session_date || '';
      const db = b.session_date || '';
      if (da !== db) return db.localeCompare(da);
      return (b.start_time || '').localeCompare(a.start_time || '');
    });
    if (sessionList.length > 0) {
      const s = sessionList[0];
      const gamesPlayedAtSession = clubRows.filter(
        (r) => r.session_games?.session_id === s.id
      ).length;
      lastSession = {
        id: s.id,
        name: s.name || 'Session',
        sessionDate: s.session_date,
        startTime: s.start_time,
        sessionStatus: s.session_status,
        gamesPlayedAtSession
      };
    }

    const recentGames = clubRows.slice(0, 25).map((row) => {
      const sg = row.session_games;
      const sess = sg?.sessions;
      const typeName = sg?.game_types?.name || 'Game';
      const sessionLabel = sess
        ? (sess.name || 'Session')
        : 'Tournament / club match';
      const when = sess?.session_date
        ? sess.session_date
        : (sg?.created_at || row.created_at);
      return {
        id: row.id,
        playedAt: row.created_at,
        sessionName: sessionLabel,
        sessionDate: when,
        sessionTime: sess?.start_time || null,
        gameTypeName: typeName,
        score: `${sg?.team_1_score ?? '—'}–${sg?.team_2_score ?? '—'}`,
        teamNumber: row.team_number,
        outcome: outcomeLabel(row, sg),
        pairType: row.pair_type || '—'
      };
    });

    return {
      guest: {
        id: guestData.id,
        first_name: guestData.first_name,
        last_name: guestData.last_name,
        gender: guestData.gender,
        created_at: guestData.created_at,
        guest_invited_email: guestData.guest_invited_email
      },
      totals: {
        totalSessions: sessionIds.size,
        totalMixedGames,
        totalLevelsGames,
        totalSinglesGames,
        totalUnbalancedGames,
        totalGamesPlayed:
          totalMixedGames + totalLevelsGames + totalSinglesGames + totalUnbalancedGames
      },
      lastSession,
      recentGames
    };
  } catch (error) {
    console.error('[GUEST] Error fetching guest identification summary:', error);
    throw error;
  }
};

/**
 * Soft delete a guest player
 * @param {string} guestId - Guest player ID
 * @param {string} deletedBy - Admin player ID who is deleting
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} - Success/error result
 */
export const softDeleteGuest = async (guestId, deletedBy, clubId) => {
  try {
    // First verify the guest belongs to the club and is a guest
    const { data: guestCheck, error: checkError } = await supabase
      .from('players')
      .select(`
        id,
        is_guest,
        players_club!inner(
          club_id,
          is_active
        )
      `)
      .eq('id', guestId)
      .eq('is_guest', true)
      .eq('players_club.club_id', clubId)
      .eq('players_club.is_active', true)
      .single();

    if (checkError || !guestCheck) {
      throw new Error('Guest not found or does not belong to this club');
    }

    // Check if already deleted
    if (guestCheck.deletion_requested_at) {
      throw new Error('Guest has already been marked for deletion');
    }

    // Update players table with soft delete fields
    const { data: playerData, error: playerError } = await supabase
      .from('players')
      .update({
        deletion_requested_at: new Date().toISOString(),
        deletion_requested_by: deletedBy,
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', guestId)
      .eq('is_guest', true)
      .select()
      .single();

    if (playerError) throw playerError;

    // Update players_club table to set is_active = false
    const { error: clubError } = await supabase
      .from('players_club')
      .update({
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('player_id', guestId)
      .eq('club_id', clubId);

    if (clubError) throw clubError;

    return { success: true, data: playerData };
  } catch (error) {
    console.error('[GUEST] Error soft deleting guest:', error);
    throw error;
  }
};

/**
 * Restore a soft-deleted guest player
 * @param {string} guestId - Guest player ID
 * @param {string} clubId - Club ID
 * @returns {Promise<Object>} - Success/error result
 */
export const restoreGuest = async (guestId, clubId) => {
  try {
    // Verify the guest exists and is marked for deletion
    const { data: guestCheck, error: checkError } = await supabase
      .from('players')
      .select(`
        id,
        is_guest,
        deletion_requested_at,
        players_club!inner(
          club_id,
          is_active
        )
      `)
      .eq('id', guestId)
      .eq('is_guest', true)
      .eq('players_club.club_id', clubId)
      .single();

    if (checkError || !guestCheck) {
      throw new Error('Guest not found or does not belong to this club');
    }

    if (!guestCheck.deletion_requested_at) {
      throw new Error('Guest is not marked for deletion');
    }

    // Clear deletion fields and restore active status
    const { data: playerData, error: playerError } = await supabase
      .from('players')
      .update({
        deletion_requested_at: null,
        deletion_requested_by: null,
        is_active: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', guestId)
      .eq('is_guest', true)
      .select()
      .single();

    if (playerError) throw playerError;

    // Restore players_club is_active status
    const { error: clubError } = await supabase
      .from('players_club')
      .update({
        is_active: true,
        updated_at: new Date().toISOString()
      })
      .eq('player_id', guestId)
      .eq('club_id', clubId);

    if (clubError) throw clubError;

    return { success: true, data: playerData };
  } catch (error) {
    console.error('[GUEST] Error restoring guest:', error);
    throw error;
  }
};

const ELO_MIN = 100;
const ELO_MAX = 4000;

/**
 * Set starting ELO for a guest at this club/season. Only allowed while the guest has
 * zero recorded games at this club (all seasons).
 *
 * @param {string} guestId
 * @param {string} clubId
 * @param {string} seasonId
 * @param {{ mixed_elo_rating: number|string, levels_elo_rating: number|string, singles_elo_rating: number|string }} ratings
 * @returns {Promise<{ id: string, mixed_elo_rating: number, levels_elo_rating: number, singles_elo_rating: number }>}
 */
export const updateGuestEloRatingsForClubSeason = async (guestId, clubId, seasonId, ratings) => {
  try {
    if (!guestId || !clubId || !seasonId) {
      throw new Error('Guest, club, and season are required to update ELO');
    }

    const parseRating = (value, label) => {
      const n = parseInt(String(value).trim(), 10);
      if (Number.isNaN(n) || n < ELO_MIN || n > ELO_MAX) {
        throw new Error(`${label} must be a whole number between ${ELO_MIN} and ${ELO_MAX}`);
      }
      return n;
    };

    const mixed_elo_rating = parseRating(ratings.mixed_elo_rating, 'Mixed ELO');
    const levels_elo_rating = parseRating(ratings.levels_elo_rating, 'Levels ELO');
    const singles_elo_rating = parseRating(ratings.singles_elo_rating, 'Singles ELO');

    const { data: player, error: playerError } = await supabase
      .from('players')
      .select('id, is_guest')
      .eq('id', guestId)
      .eq('is_guest', true)
      .single();

    if (playerError || !player) {
      throw new Error('Guest not found');
    }

    const { data: clubRow, error: clubErr } = await supabase
      .from('players_club')
      .select('id')
      .eq('player_id', guestId)
      .eq('club_id', clubId)
      .eq('is_active', true)
      .maybeSingle();

    if (clubErr || !clubRow) {
      throw new Error('Guest is not an active member of this club');
    }

    const { data: eloRows, error: eloErr } = await supabase
      .from('player_club_elo_ratings')
      .select(
        'id, season_id, mixed_games_played, levels_games_played, singles_games_played, unbalanced_games_played'
      )
      .eq('player_id', guestId)
      .eq('club_id', clubId);

    if (eloErr) throw eloErr;

    const totalGames = (eloRows || []).reduce(
      (sum, r) =>
        sum +
        (r.mixed_games_played || 0) +
        (r.levels_games_played || 0) +
        (r.singles_games_played || 0) +
        (r.unbalanced_games_played || 0),
      0
    );

    if (totalGames > 0) {
      throw new Error('ELO can only be edited before this guest plays any games at this club');
    }

    const seasonRow = (eloRows || []).find((r) => r.season_id === seasonId);
    const payload = {
      mixed_elo_rating,
      levels_elo_rating,
      singles_elo_rating,
      updated_at: new Date().toISOString()
    };

    if (seasonRow) {
      const { data, error } = await supabase
        .from('player_club_elo_ratings')
        .update(payload)
        .eq('id', seasonRow.id)
        .select('id, mixed_elo_rating, levels_elo_rating, singles_elo_rating')
        .single();

      if (error) throw error;
      return data;
    }

    const insertRow = {
      player_id: guestId,
      club_id: clubId,
      season_id: seasonId,
      mixed_elo_rating,
      levels_elo_rating,
      singles_elo_rating,
      mixed_games_played: 0,
      levels_games_played: 0,
      singles_games_played: 0,
      unbalanced_games_played: 0
    };

    const { data: inserted, error: insErr } = await supabase
      .from('player_club_elo_ratings')
      .insert([insertRow])
      .select('id, mixed_elo_rating, levels_elo_rating, singles_elo_rating')
      .single();

    if (insErr) throw insErr;
    return inserted;
  } catch (error) {
    console.error('[GUEST] Error updating guest ELO:', error);
    throw error;
  }
};

/**
 * Update guest information
 * @param {string} guestId - Guest player ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - Updated guest
 */
export const updateGuest = async (guestId, updates) => {
  try {
    const { data, error } = await supabase
      .from('players')
      .update(updates)
      .eq('id', guestId)
      .eq('is_guest', true)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('[GUEST] Error updating guest:', error);
    throw error;
  }
};

/**
 * Remove email from guest account
 * Only allowed if guest is not merged/inactive
 * @param {string} guestId - Guest player ID
 * @returns {Promise<Object>} - Updated guest
 */
export const removeEmailFromGuest = async (guestId) => {
  try {
    // First check if guest exists and is not merged
    const { data: guest, error: guestError } = await supabase
      .from('players')
      .select('id, is_guest, merged_at, merged_into_user_id')
      .eq('id', guestId)
      .eq('is_guest', true)
      .single();

    if (guestError) {
      console.error('[GUEST] Error fetching guest:', guestError);
      throw new Error('Guest not found');
    }

    if (guest.merged_at || guest.merged_into_user_id) {
      throw new Error('Cannot remove email from a merged guest account');
    }

    // Remove email assignment
    const { data: updatedGuest, error: updateError } = await supabase
      .from('players')
      .update({
        guest_invited_email: null,
        guest_email_assigned_at: null,
        guest_email_assigned_by: null
      })
      .eq('id', guestId)
      .eq('is_guest', true)
      .is('merged_at', null) // Ensure not merged
      .select()
      .single();

    if (updateError) {
      console.error('[GUEST] Error removing email:', updateError);
      throw updateError;
    }

    console.log('[GUEST] Email removed successfully:', updatedGuest);
    return updatedGuest;
  } catch (error) {
    console.error('[GUEST] Error in removeEmailFromGuest:', error);
    throw error;
  }
};

export const EXISTING_MEMBER_NO_GAMES = 'EXISTING_MEMBER_NO_GAMES';

const createGuestAssignError = (message, extras = {}) => {
  const error = new Error(message);
  Object.assign(error, extras);
  return error;
};

/**
 * Count recorded games for a player at one club.
 * Signups and default Elo rows do not count.
 * @param {string} playerId
 * @param {string} clubId
 * @returns {Promise<number>}
 */
export const countPlayerClubGames = async (playerId, clubId) => {
  if (!playerId || !clubId) return 0;

  const { count, error } = await supabase
    .from('players_session_games')
    .select('id, session_games!inner(club_id)', { count: 'exact', head: true })
    .eq('player_id', playerId)
    .eq('session_games.club_id', clubId);

  if (error) throw error;
  return count || 0;
};

/**
 * Assign email to guest player for future account linking
 * @param {string} guestId - Guest player ID
 * @param {string} email - Email address to assign
 * @param {string} assignedBy - ID of the user assigning the email
 * @param {string} clubId - Optional club ID for club-specific validation
 * @param {Object} [options]
 * @param {boolean} [options.allowExistingMemberWithoutGames] - Allow assign when the email owner is already a member with no games at this club
 * @param {boolean} [options.skipMergeNotification] - Skip guest-merge notification (used when merge follows immediately)
 * @returns {Promise<Object>} - Updated guest
 */
export const assignEmailToGuest = async (guestId, email, assignedBy, clubId = null, options = {}) => {
  try {
    console.log('[GUEST] Assigning email to guest:', { guestId, email, clubId });
    
    // Check if guest exists and is actually a guest
    const { data: existingGuest, error: guestError } = await supabase
      .from('players')
      .select('id, is_guest, guest_invited_email, first_name, last_name')
      .eq('id', guestId)
      .eq('is_guest', true)
      .single();

    if (guestError) {
      console.error('[GUEST] Error fetching guest:', guestError);
      throw new Error('Guest not found');
    }

    if (existingGuest.guest_invited_email) {
      throw new Error('Guest already has an email assigned');
    }

    // Get club ID if not provided - get from guest's active club membership
    let targetClubId = clubId;
    if (!targetClubId) {
      const { data: guestClub, error: clubError } = await supabase
        .from('players_club')
        .select('club_id')
        .eq('player_id', guestId)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      
      if (clubError) {
        console.error('[GUEST] Error fetching guest club:', clubError);
        throw new Error('Failed to determine guest club');
      }
      
      if (guestClub) {
        targetClubId = guestClub.club_id;
      }
    }

    // Email validation is club-specific:
    // 1. Allow emails belonging to users at different clubs (that's fine)
    // 2. Only block if email is assigned to another guest at the SAME club
    // 3. Only block if there's a merged guest (archived) at the SAME club with this email

    if (targetClubId) {
      // Check if email is already assigned to another guest at the same club
      // First, get all guests with this email
      const { data: guestsWithEmail, error: emailCheckError } = await supabase
        .from('players')
        .select('id, first_name, last_name')
        .eq('guest_invited_email', email.toLowerCase().trim())
        .eq('is_guest', true)
        .neq('id', guestId);

      if (emailCheckError) {
        console.error('[GUEST] Error checking existing guest email:', emailCheckError);
        throw emailCheckError;
      }

      if (guestsWithEmail && guestsWithEmail.length > 0) {
        // Check if any of these guests are at the same club
        const guestIds = guestsWithEmail.map(g => g.id);
        const { data: clubMemberships, error: membershipError } = await supabase
          .from('players_club')
          .select('player_id, club_id, is_active')
          .in('player_id', guestIds)
          .eq('club_id', targetClubId)
          .eq('is_active', true);

        if (membershipError) {
          console.error('[GUEST] Error checking club memberships:', membershipError);
          throw membershipError;
        }

        if (clubMemberships && clubMemberships.length > 0) {
          const conflictingGuestId = clubMemberships[0].player_id;
          const conflictingGuest = guestsWithEmail.find(g => g.id === conflictingGuestId);
          throw new Error(`Email already assigned to guest: ${conflictingGuest?.first_name || ''} ${conflictingGuest?.last_name || ''}`);
        }
      }

      // Check if there's a merged guest (archived) at the same club with this email
      // A merged guest has merged_into_user_id set and was previously at this club
      const { data: mergedGuests, error: mergedCheckError } = await supabase
        .from('players')
        .select('id, first_name, last_name, merged_into_user_id')
        .eq('guest_invited_email', email.toLowerCase().trim())
        .eq('is_guest', true)
        .not('merged_into_user_id', 'is', null);

      if (mergedCheckError) {
        console.error('[GUEST] Error checking merged guest:', mergedCheckError);
        throw mergedCheckError;
      }

      if (mergedGuests && mergedGuests.length > 0) {
        // Check if any merged guest was at the same club
        const mergedGuestIds = mergedGuests.map(g => g.id);
        const { data: mergedMemberships, error: mergedMembershipError } = await supabase
          .from('players_club')
          .select('player_id, club_id')
          .in('player_id', mergedGuestIds)
          .eq('club_id', targetClubId);

        if (mergedMembershipError) {
          console.error('[GUEST] Error checking merged guest memberships:', mergedMembershipError);
          throw mergedMembershipError;
        }

        if (mergedMemberships && mergedMemberships.length > 0) {
          throw new Error(`This email was previously assigned to a guest that has been merged. Please contact support if you need to reassign this email.`);
        }
      }

      // Check if email belongs to a registered user who is already in the club and has played games
      // This prevents merge conflicts where both the user and guest would have game history
      const { data: existingUser, error: userCheckError } = await supabase
        .from('players')
        .select('id, first_name, last_name, email, is_guest')
        .eq('email', email.toLowerCase().trim())
        .eq('is_guest', false)
        .maybeSingle();

      if (userCheckError) {
        console.error('[GUEST] Error checking existing user:', userCheckError);
        throw userCheckError;
      }

      if (existingUser) {
        // Check if this user is a member of the club
        const { data: userMembership, error: membershipCheckError } = await supabase
          .from('players_club')
          .select('player_id, club_id, is_active')
          .eq('player_id', existingUser.id)
          .eq('club_id', targetClubId)
          .eq('is_active', true)
          .maybeSingle();

        if (membershipCheckError) {
          console.error('[GUEST] Error checking user membership:', membershipCheckError);
          throw membershipCheckError;
        }

        if (userMembership) {
          const userName = `${existingUser.first_name} ${existingUser.last_name || ''}`.trim();
          const clubGameCount = await countPlayerClubGames(existingUser.id, targetClubId);

          if (clubGameCount > 0) {
            throw createGuestAssignError(
              `Cannot assign email: ${userName} is already a member of this club and has ${clubGameCount} game${clubGameCount === 1 ? '' : 's'} here.`,
              {
                code: 'EXISTING_MEMBER_HAS_GAMES',
                memberId: existingUser.id,
                memberName: userName,
                clubGameCount,
              }
            );
          }

          if (!options.allowExistingMemberWithoutGames) {
            throw createGuestAssignError(
              `${userName} is already a member of this club and has not played here. Confirm to merge this guest into their account.`,
              {
                code: EXISTING_MEMBER_NO_GAMES,
                memberId: existingUser.id,
                memberName: userName,
              }
            );
          }
        }
      }
    } else {
      // If no club ID, fall back to global check (for backwards compatibility)
      // Check if email is already assigned to another guest
      const { data: existingGuestWithEmail, error: emailCheckError } = await supabase
        .from('players')
        .select('id, first_name, last_name')
        .eq('guest_invited_email', email.toLowerCase().trim())
        .eq('is_guest', true)
        .neq('id', guestId)
        .maybeSingle();

      if (emailCheckError) {
        console.error('[GUEST] Error checking existing guest email:', emailCheckError);
        throw emailCheckError;
      }

      if (existingGuestWithEmail) {
        throw new Error(`Email already assigned to guest: ${existingGuestWithEmail.first_name} ${existingGuestWithEmail.last_name}`);
      }
    }

    // Assign email to guest
    // Clear declined status if it exists (decline was for previous email, not this new one)
    const { data: updatedGuest, error: updateError } = await supabase
      .from('players')
      .update({
        guest_invited_email: email,
        guest_email_assigned_at: new Date().toISOString(),
        guest_email_assigned_by: assignedBy,
        guest_merge_declined_at: null,  // Clear decline status - decline was for previous email
        guest_merge_declined_by: null   // Clear who declined - decline was for previous email
      })
      .eq('id', guestId)
      .select()
      .single();

    if (updateError) {
      console.error('[GUEST] Error updating guest with email:', updateError);
      throw updateError;
    }

    console.log('[GUEST] Email assigned successfully:', updatedGuest);

    if (!options.skipMergeNotification) {
      try {
        await supabase.rpc('check_and_create_guest_notifications', {
          p_user_id: null, // Will be found by email match in trigger
          p_email: email
        });
      } catch (err) {
        console.error('[GUEST] Error triggering notification check:', err);
        // Don't fail email assignment if notification check fails
      }
    }
    
    return updatedGuest;
  } catch (error) {
    console.error('[GUEST] Error in assignEmailToGuest:', error);
    throw error;
  }
};

/**
 * Remove guest from club (soft delete - keeps player record for data integrity)
 * @param {string} guestId - Guest player ID  
 * @param {string} clubId - Club ID
 * @returns {Promise<void>}
 */
export const removeGuestFromClub = async (guestId, clubId) => {
  try {
    const { error } = await supabase
      .from('players_club')
      .update({ is_active: false })
      .eq('player_id', guestId)
      .eq('club_id', clubId);

    if (error) throw error;
  } catch (error) {
    console.error('[GUEST] Error removing guest from club:', error);
    throw error;
  }
};

/**
 * Initialize ELO ratings for existing players who don't have them yet
 * @param {string} clubId - Club ID
 * @param {string} seasonId - Season ID
 * @returns {Promise<Object>} - Summary of initialized players
 */
export const initializeEloRatingsForClub = async (clubId, seasonId) => {
  try {
    console.log('[ELO] Initializing missing ELO ratings for club:', { clubId, seasonId });
    
    // First, let's get all active players in the club
    const { data: allPlayers, error: playersError } = await supabase
      .from('players_club')
      .select(`
        player_id,
        players(first_name, last_name)
      `)
      .eq('club_id', clubId)
      .eq('is_active', true);

    if (playersError) {
      console.error('[ELO] Error querying all players:', playersError);
      throw playersError;
    }

    console.log(`[ELO] Found ${allPlayers?.length || 0} total active players in club`);

    // Get existing ELO ratings for this club/season
    const { data: existingEloRatings, error: eloError } = await supabase
      .from('player_club_elo_ratings')
      .select('player_id')
      .eq('club_id', clubId)
      .eq('season_id', seasonId);

    if (eloError) {
      console.error('[ELO] Error querying existing ELO ratings:', eloError);
      throw eloError;
    }

    console.log(`[ELO] Found ${existingEloRatings?.length || 0} existing ELO ratings`);

    // Find players without ELO ratings
    const existingPlayerIds = new Set(existingEloRatings?.map(r => r.player_id) || []);
    const playersWithoutElo = allPlayers?.filter(p => !existingPlayerIds.has(p.player_id)) || [];

    if (playersWithoutElo.length === 0) {
      console.log('[ELO] All players already have ELO ratings');
      return { initialized: 0, skipped: existingEloRatings?.length || 0 };
    }

    console.log(`[ELO] Found ${playersWithoutElo.length} players without ELO ratings:`, 
      playersWithoutElo.map(p => `${p.players.first_name} ${p.players.last_name}`));

    const clubDefaultElo = await fetchClubDefaultStartingElo(clubId);

    // Initialize ELO ratings for each player
    const eloRecords = playersWithoutElo.map(pc => ({
      player_id: pc.player_id,
      club_id: clubId,
      season_id: seasonId,
      mixed_elo_rating: clubDefaultElo,
      levels_elo_rating: clubDefaultElo,
      singles_elo_rating: 1000,
      mixed_games_played: 0,
      levels_games_played: 0,
      singles_games_played: 0,
      unbalanced_games_played: 0
    }));

    console.log('[ELO] About to insert ELO records:', eloRecords);

    const { data: insertedData, error: insertError } = await supabase
      .from('player_club_elo_ratings')
      .insert(eloRecords)
      .select();

    if (insertError) {
      console.error('[ELO] Error inserting ELO ratings:', insertError);
      throw insertError;
    }

    console.log(`[ELO] Successfully initialized ELO ratings for ${playersWithoutElo.length} players`);
    console.log('[ELO] Inserted data:', insertedData);
    return { 
      initialized: playersWithoutElo.length, 
      skipped: existingEloRatings?.length || 0,
      players: playersWithoutElo.map(pc => pc.players)
    };
  } catch (error) {
    console.error('[ELO] Error in initializeEloRatingsForClub:', error);
    throw error;
  }
};
